import mustache from "mustache";
import { TareaResponse } from "../../../../modulos/configuracion/rastro/response/TareaResponse";
import { fx_createTareaGrpcService } from "../../../../modulos/configuracion/rastro/services/TareaGrpcService";
import { TareaDeleteGrpcService } from "../../../../modulos/configuracion/rastro/services/TareaDeleteGrpcService";
import { TareaEjecucionRequest } from "../../../../modulos/rastro/request/TareaEjecucionRequest";
import { IFormField } from "../../../definitions/form/IFormField";
import { ConfirmModalRender } from "../../../implementations/modal/ConfirmModalRender";
import { TareaRenameModal } from "../../configuracion/rastro/modal/TareaRenameModal";
import createOptionTemplate from "./tareaSelectorCreateOptionTemplate.html";
import optionTemplate from "./tareaSelectorOptionTemplate.html";
import template from "./tareaSelectorFieldTemplate.html";

mustache.parse(template);
mustache.parse(optionTemplate);
mustache.parse(createOptionTemplate);

/** Contador para ids únicos por instancia. */
let TAREA_SELECTOR_ID = 0;

/** Campo selector de tarea (`IFormField`, estilo autocomplete de un solo
 *  valor — no el de productos, que agrega varios). Al elegir una opción
 *  muestra su nombre en el input (no se limpia); si el texto escrito no
 *  matchea ninguna, ofrece "+ Crear". Las filas de tareas del cliente (no las
 *  globales) llevan editar/eliminar inline, abriendo `TareaRenameModal` o
 *  confirmando el borrado. Siempre requerido (el request no admite null). */
export class TareaSelectorField implements IFormField {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _fieldId: number;
  /** Catálogo completo cargado. */
  private _items: TareaResponse[] = [];
  /** Items actualmente visibles en el dropdown (paralelo a los data-index). */
  private _visible: TareaResponse[] = [];
  private _disposed = false;

  /**
   * @param _request      Request de la ejecución (se escribe `tareaId`).
   * @param _clienteId    Cliente dueño de la finca (catálogo + qué tareas son
   *                      editables/eliminables desde acá).
   * @param _nombreInicial Nombre de la tarea ya asignada (si la hay), para
   *                       mostrarlo de entrada sin esperar el catálogo.
   */
  constructor(
    private readonly _request: TareaEjecucionRequest,
    private readonly _clienteId: string,
    private readonly _nombreInicial: string | null,
  ) {
    TAREA_SELECTOR_ID += 1;
    this._fieldId = TAREA_SELECTOR_ID;
  }

  getId(): string {
    return `tarea-selector-${this._fieldId}`;
  }

  render(): string {
    return mustache.render(template, {
      id: this._fieldId,
      valorInicial: this._nombreInicial ?? "",
    });
  }

  connect(): void {
    this._input = document.getElementById(
      `tarea-selector-input-${this._fieldId}`,
    ) as HTMLInputElement | null;
    this._dropdown = document.getElementById(
      `tarea-selector-dropdown-${this._fieldId}`,
    );
    this._error = document.getElementById(`tarea-selector-error-${this._fieldId}`);

    this._input?.addEventListener("input", this._onInput);
    this._input?.addEventListener("focus", this._onInput);
    this._input?.addEventListener("blur", this._onBlur);
    this._dropdown?.addEventListener("mousedown", this._onDropdownMouseDown);
    this._dropdown?.addEventListener("click", this._onDropdownClick);

    void this._loadCatalogoAsync();
  }

  dispose(): void {
    this._disposed = true;
    this._input?.removeEventListener("input", this._onInput);
    this._input?.removeEventListener("focus", this._onInput);
    this._input?.removeEventListener("blur", this._onBlur);
    this._dropdown?.removeEventListener("mousedown", this._onDropdownMouseDown);
    this._dropdown?.removeEventListener("click", this._onDropdownClick);
  }

  getError(): string | null {
    return this._request.tareaId === "" ? "Debe seleccionar una tarea" : null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    this._input?.focus();
  }

  disabled(): void {
    if (this._input) this._input.disabled = true;
  }

  enabled(): void {
    if (this._input) this._input.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _dropdown: HTMLElement | null = null;
  private _error: HTMLElement | null = null;

  /** Carga el catálogo de tareas visibles del cliente (globales + propias). */
  private async _loadCatalogoAsync(): Promise<void> {
    const items = await fx_createTareaGrpcService(
      this._clienteId,
    ).getListAsync();
    if (this._disposed) return;
    this._items = items;

    // Si no llegó nombre inicial (o quedó desactualizado), resolverlo del
    // catálogo ya cargado.
    const tareaId = this._request.tareaId;
    if (tareaId && this._input && document.activeElement !== this._input) {
      const actual = items.find((t) => t.id === tareaId);
      if (actual) this._input.value = actual.nombre;
    }
  }

  /** Filtra por texto y arma el dropdown (opciones + "crear", siempre presente). */
  private _onInput = (): void => {
    const textoCrudo = this._input?.value ?? "";
    const texto = textoCrudo.trim().toLowerCase();
    this._visible = this._items
      .filter((t) => t.nombre.toLowerCase().includes(texto))
      .slice(0, 50);
    this._renderDropdown(textoCrudo.trim());
  };

  /** Al perder foco: si no quedó nada seleccionado que coincida, restaura el
   *  input al nombre de la tarea actualmente asignada (o lo vacía). */
  private _onBlur = (): void => {
    this._hideDropdown();
    if (!this._input) return;
    const actual = this._items.find((t) => t.id === this._request.tareaId);
    this._input.value = actual?.nombre ?? "";
  };

  /** Previene el blur del input al clickear dentro del dropdown. */
  private _onDropdownMouseDown = (e: Event): void => {
    e.preventDefault();
  };

  /** Delegado del dropdown: editar/eliminar/crear/seleccionar según lo
   *  clickeado (en ese orden de prioridad, por especificidad del elemento). */
  private _onDropdownClick = (e: Event): void => {
    const target = e.target as HTMLElement;

    const editBtn = target.closest<HTMLElement>("[data-edit]");
    if (editBtn) {
      const tarea = this._visible[Number(editBtn.dataset.edit)];
      if (tarea) this._openEdit(tarea);
      return;
    }

    const deleteBtn = target.closest<HTMLElement>("[data-delete]");
    if (deleteBtn) {
      const tarea = this._visible[Number(deleteBtn.dataset.delete)];
      if (tarea) this._confirmDelete(tarea);
      return;
    }

    const createOption = target.closest<HTMLElement>("[data-create]");
    if (createOption) {
      this._openCreate((this._input?.value ?? "").trim());
      return;
    }

    const option = target.closest<HTMLElement>("[data-index]");
    if (option) {
      const tarea = this._visible[Number(option.dataset.index)];
      if (tarea) this._selectTarea(tarea);
    }
  };

  /** Asigna la tarea elegida, muestra su nombre y cierra el dropdown. */
  private _selectTarea(tarea: TareaResponse): void {
    this._request.tareaId = tarea.id;
    if (this._input) this._input.value = tarea.nombre;
    this._hideDropdown();
    this.showError();
  }

  /** Abre el modal de edición de una tarea del cliente. */
  private _openEdit(tarea: TareaResponse): void {
    this._hideDropdown();
    new TareaRenameModal(this._clienteId, tarea, (actualizada) => {
      this._items = this._items.map((t) =>
        t.id === actualizada.id ? actualizada : t,
      );
      if (this._request.tareaId === actualizada.id && this._input) {
        this._input.value = actualizada.nombre;
      }
    }).init();
  }

  /** Abre el modal de creación, precargando el texto ya escrito. */
  private _openCreate(nombreInicial: string): void {
    this._hideDropdown();
    new TareaRenameModal(this._clienteId, null, (creada) => {
      this._items = [...this._items, creada];
      this._selectTarea(creada);
    }, nombreInicial).init();
  }

  /** Confirma y elimina una tarea del cliente; si era la asignada, la
   *  desasigna (queda "" hasta que se elija otra). */
  private _confirmDelete(tarea: TareaResponse): void {
    this._hideDropdown();
    new ConfirmModalRender({
      title: "Eliminar tarea",
      message: `¿Está seguro que desea eliminar la tarea <strong>${tarea.nombre}</strong>? Las ejecuciones que ya la tenían asignada conservan la referencia.`,
      onConfirm: async () => {
        await new TareaDeleteGrpcService().deleteAsync(tarea);
        this._items = this._items.filter((t) => t.id !== tarea.id);
        if (this._request.tareaId === tarea.id) {
          this._request.tareaId = "";
          if (this._input) this._input.value = "";
          this.showError();
        }
      },
    }).init();
  }

  /** Pinta las filas visibles + la opción "crear" (siempre presente). */
  private _renderDropdown(textoOriginal: string): void {
    if (!this._dropdown) return;

    const filas = this._visible
      .map((tarea, index) =>
        mustache.render(optionTemplate, {
          index,
          nombre: tarea.nombre,
          esDelCliente: tarea.clienteId === this._clienteId,
        }),
      )
      .join("");

    // La opción "crear" siempre aparece (con el texto escrito, o genérica si
    // el input está vacío) — no depende de si ya hay una tarea que matchee.
    const crear = mustache.render(createOptionTemplate, {
      texto: textoOriginal,
    });

    this._dropdown.innerHTML = filas + crear;
    this._dropdown.style.display = "";
  }

  /** Cierra el dropdown y descarta los visibles. */
  private _hideDropdown(): void {
    if (this._dropdown) {
      this._dropdown.style.display = "none";
      this._dropdown.innerHTML = "";
    }
    this._visible = [];
  }
}
